import {css, html, LitElement, nothing} from 'lit';
import {map} from 'lit/directives/map.js';
import {customElement, property} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';

interface SourceEntry {
  src: string;
  type: string;
  media?: string;
}

@customElement('hover-slider-slide')
export class HoversliderSlideComponent extends LitElement {
  static styles = css`
    :host {
      position: relative;
      height: 100%;
      flex-basis: 0;
      flex-grow: 1;
      transition: flex-grow ease-in-out 0.8s;
      overflow: hidden;
      background: #ddd;
    }

    *, *:before, *:after {
      box-sizing: border-box;
    }

    video {
      position: absolute;
      /* Chrome rounding work around: */
      width: calc(100% + 1px);
      height: 100%;
      object-fit: cover;
      transition: transform ease-out .3s;
      transition-delay: 0.5s;
    }

    video.active {
      transform: scale(1.03);
    }

    @media only screen and (max-width: 1023px) {
      :host {
        flex-basis: 100%;
        height: auto;
      }

      video {
        transition-delay: 0s;
        width: 40%;
      }

      video.active {
        transform: none;
      }

      ::slotted(*) {
        /*position: relative !important;*/
      }
    }
  `;

  @property({type: Array}) sources: SourceEntry[] = [];

  @property({type: Boolean}) play: boolean = false;

  @property({type: Boolean, reflect: true}) active: boolean = false;

  @property() poster: string = '';

  requestUpdate(name?: PropertyKey, oldValue?: unknown) {
    if (name === 'play' && this.shadowRoot && this.play !== oldValue) {
      const video = <HTMLVideoElement>this.shadowRoot!.querySelector('video');
      if (video) {
        if (this.play) {
          // @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
          // state HAVE_FUTURE_DATA is unfortunately never reached on iPhone until play() is called.
          // But calling play() too early on Edge and Chrome prevents playback...
          if (video.readyState >= 3) {
            video.playbackRate = 1.;
            video.play().then(
              r => {
                if (!this.play) {
                  video.pause();
                }
              },
            );
          } else {
            video.load();
          }
        } else {
          video.playbackRate = .8;
          setTimeout(
            () => {
              if (!this.play) {
                video.pause();
              }
            },
            500,
          );
        }
        return;
      }
    }
    return super.requestUpdate(name, oldValue);
  }


  render() {
    return html`
      <video ?muted=${true} ?loop=${true} ?playsinline=${true}
             @canplaythrough=${this._handleCanplaythrough}
             class=${classMap({active: this.active})}
             poster="${this.poster}"
      >
        ${
          map(
            this.sources,
            source => html`
              <source
                src=${source.src}
                type=${source.type}
                media="${source.media || nothing}"
              >
            `,
          )
        }
      </video>
      <slot></slot>
    `;
  }

  private _handleCanplaythrough(ev: Event) {
    // not thrown on iPhone without a prior play()
    const video = <HTMLVideoElement>ev.target;

    if (this.play) {
      video.playbackRate = 1.;
      video.play().then(
        r => {
        },
      );
    }
  }
}
